/**
 * `ResponsiveAsset` — drop-in `<img>` replacement with automatic Cloudflare
 * Image Resizing for asset-CDN URLs and video poster support.
 *
 * Behaviour:
 *  1. Measures the element's CSS width (via `ResizeObserver`) once mounted.
 *  2. Snaps the width to a fixed bucket (see `WIDTH_BUCKETS`) to maximise
 *     cache-hit rate across pages and viewport sizes.
 *  3. Multiplies by `devicePixelRatio` (capped at 2) for retina sharpness.
 *  4. For URLs hosted on the asset CDN (`companio.chat/images/`), appends
 *     resize query params (`?w=<W>&f=auto&fit=cover&…`) so the image-worker
 *     can apply Cloudflare Image Resizing via `cf.image` and deliver a
 *     correctly-sized WebP/AVIF.
 *  5. When `src` is a video URL (`.mp4`, `.webm`, `.mov`), renders a
 *     `<video>` element with a poster frame extracted via Cloudflare Media
 *     Transformations (`?mode=frame&width=<W>&format=jpg`).  The video
 *     auto-plays muted and loops, behaving like an animated image.
 *  6. External URLs (Unsplash, third-party CDNs, etc.) pass through unchanged.
 *
 * The component also generates a `srcSet` with 1x and 2x descriptors for
 * asset-CDN URLs so the browser can pick the best density autonomously.
 */
import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Ordered width buckets. Kept small & power-of-two-ish to maximise CDN hits. */
const WIDTH_BUCKETS = [64, 128, 192, 256, 384, 512, 640, 750, 828, 1080, 1200, 1920, 2048] as const;

/**
 * CDN path segment that signals a URL is eligible for Cloudflare Image
 * Resizing.  The image-worker is deployed on `/images/*` across multiple
 * zones (companio.chat, funnel-admin.nextday.ai, etc.) so we match on the
 * path rather than a single hardcoded domain.
 */
const ASSET_CDN_PATHS = ["/images/", "/api/admin/assets/file/"] as const;

/** Maximum DPR we'll serve — 3x screens get 2x assets (diminishing returns). */
const MAX_DPR = 2;

/** Video file extensions recognised for automatic video rendering. */
const VIDEO_EXTENSIONS = /\.(mp4|webm|mov)(\?|#|$)/i;

// ---------------------------------------------------------------------------
// Helpers (exported for testing)
// ---------------------------------------------------------------------------

/** Snap a pixel width up to the nearest bucket. */
export function snapToBucket(width: number): number {
  for (const bucket of WIDTH_BUCKETS) {
    if (bucket >= width) return bucket;
  }
  return WIDTH_BUCKETS[WIDTH_BUCKETS.length - 1]!;
}

/** Known hostnames where the image-worker is deployed. */
const ASSET_CDN_HOSTS = ["companio.chat", "funnel-admin.nextday.ai"] as const;

/** Returns true when the URL can be routed through Cloudflare Image Resizing. */
export function isResizableUrl(src: string): boolean {
  try {
    const url = new URL(src);
    return (
      url.protocol === "https:" &&
      ASSET_CDN_HOSTS.some((h) => url.hostname === h) &&
      ASSET_CDN_PATHS.some((p) => url.pathname.startsWith(p))
    );
  } catch {
    return false;
  }
}

/** Returns true when `src` points to a video file based on its extension. */
export function isVideoUrl(src: string): boolean {
  return VIDEO_EXTENSIONS.test(src);
}

/**
 * Build a resize-param URL from an asset-CDN source.
 *
 * Appends query parameters that the image-worker reads to apply Cloudflare
 * Image Resizing via `cf.image` on the Worker `fetch()` sub-request.
 */
export function buildResizedUrl(src: string, widthPx: number): string {
  const sharpen = 1;
  try {
    const url = new URL(src);
    url.searchParams.set("w", String(widthPx));
    url.searchParams.set("f", "auto");
    url.searchParams.set("fit", "cover");
    url.searchParams.set("gravity", "face");
    url.searchParams.set("sharpen", String(sharpen));
    url.searchParams.set("q", "90");
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Build a poster-frame URL for a CDN-hosted video via Cloudflare Media
 * Transformations.  Appends `?mode=frame&time=0s&width=<W>&format=jpg` so
 * the image-worker redirects to `cdn-cgi/media` and returns a JPEG still.
 */
export function buildPosterUrl(src: string, widthPx: number): string {
  try {
    const url = new URL(src);
    url.searchParams.set("mode", "frame");
    url.searchParams.set("time", "0s");
    url.searchParams.set("width", String(widthPx));
    url.searchParams.set("format", "jpg");
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Build an optimized video URL via Cloudflare Media Transformations.
 * Appends `?mode=video&width=<W>&audio=false` so the worker redirects to
 * `cdn-cgi/media` and returns a re-encoded, width-optimized mp4. Funnel
 * playback is muted, so audio is stripped unless a caller (e.g. an admin
 * preview player) asks to keep it.
 */
export function buildOptimizedVideoUrl(src: string, widthPx: number, withAudio = false): string {
  try {
    const url = new URL(src);
    url.searchParams.set("mode", "video");
    url.searchParams.set("width", String(widthPx));
    url.searchParams.set("audio", withAudio ? "true" : "false");
    return url.toString();
  } catch {
    return src;
  }
}

/**
 * Return a preview-friendly URL for any asset.  For video URLs on a
 * resizable CDN host, returns a poster-frame JPEG; otherwise returns `src`
 * unchanged.  Useful for `<img>` thumbnail previews in admin config panels.
 */
export function previewUrl(src: string, widthPx = 256): string {
  if (!isResizableUrl(src)) return src;
  if (isVideoUrl(src)) return buildPosterUrl(src, widthPx);
  return buildResizedUrl(src, widthPx);
}

/** Return the effective DPR, capped to MAX_DPR. */
function getEffectiveDpr(): number {
  if (typeof window === "undefined") return 1;
  return Math.min(Math.round(window.devicePixelRatio || 1), MAX_DPR);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export type ResponsiveAssetProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "srcSet" | "sizes"
> & {
  /** Explicit display width hint in CSS pixels. When omitted the component
   *  measures its own rendered width via ResizeObserver. */
  widthHint?: number;
  /**
   * When `true`, force rendering as an `<img>` even if `src` is a video URL.
   * Useful for displaying a poster-frame thumbnail without a `<video>` tag.
   */
  posterOnly?: boolean;
};

function ResponsiveAssetImpl({
  src,
  widthHint,
  loading = "lazy",
  decoding = "async",
  posterOnly,
  ...rest
}: ResponsiveAssetProps) {
  // Props shared between <img> and <video> — extracted so we can forward
  // event handlers (onLoad, onError), layout attrs, and data-*/aria-*
  // attributes to <video> elements without passing img-only attributes
  // that cause TS errors.
  const { className, style, id, onLoad, onError, ...extraRest } = rest;
  const dataAndAriaAttrs: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(extraRest)) {
    if (key.startsWith("data-") || key.startsWith("aria-")) {
      dataAndAriaAttrs[key] = val;
    }
  }
  const videoProps = {
    className,
    style,
    id,
    onLoadedData: onLoad,
    onError,
    ...dataAndAriaAttrs,
  } as Record<string, unknown>;
  const elRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | undefined>(undefined);

  const observe = useCallback(
    (el: HTMLElement | null) => {
      if (!el || widthHint !== undefined) return;
      if (el.offsetWidth > 0) {
        setMeasuredWidth(el.offsetWidth);
        return;
      }
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width;
        if (w && w > 0) {
          setMeasuredWidth(w);
          ro.disconnect();
        }
      });
      ro.observe(el);
      return () => ro.disconnect();
    },
    [widthHint],
  );

  const setRef = useCallback(
    (el: HTMLImageElement | HTMLVideoElement | null) => {
      elRef.current = el;
      return observe(el);
    },
    [observe],
  );

  useEffect(() => {
    if (widthHint !== undefined) setMeasuredWidth(widthHint);
  }, [widthHint]);

  const isVideo = !!src && isVideoUrl(src);
  const resizable = !!src && isResizableUrl(src);

  // Non-CDN, non-video: plain <img> fallback.
  if (!src || (!resizable && !isVideo)) {
    return <img ref={setRef} src={src} loading={loading} decoding={decoding} {...rest} />;
  }

  const cssWidth = measuredWidth ?? widthHint;

  // Width not yet known — render a transparent placeholder so
  // ResizeObserver can measure the layout slot.
  if (cssWidth === undefined || cssWidth === 0) {
    return (
      <img
        ref={setRef}
        loading={loading}
        decoding={decoding}
        {...rest}
        style={{ ...rest.style, opacity: 0 }}
        aria-hidden
      />
    );
  }

  const dpr = getEffectiveDpr();
  const physicalWidth = snapToBucket(Math.ceil(cssWidth * dpr));

  // --- Video on CDN: <video> with auto-generated poster frame ---
  if (isVideo && resizable) {
    const poster = buildPosterUrl(src, physicalWidth);

    if (posterOnly) {
      return <img ref={setRef} src={poster} loading={loading} decoding={decoding} {...rest} />;
    }

    const optimizedSrc = buildOptimizedVideoUrl(src, physicalWidth);

    return (
      <video
        ref={setRef}
        src={optimizedSrc}
        poster={poster}
        autoPlay
        muted
        loop
        playsInline
        {...videoProps}
      />
    );
  }

  // --- Video not on CDN: basic <video> with preload ---
  if (isVideo) {
    if (posterOnly) {
      const seekSrc = src.includes("#") ? src : `${src}#t=0.001`;
      return (
        <video ref={setRef} src={seekSrc} preload="metadata" muted playsInline {...videoProps} />
      );
    }
    return (
      <video
        ref={setRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        {...videoProps}
      />
    );
  }

  // --- Image on CDN: optimised <img> with srcSet ---
  const w1x = snapToBucket(Math.ceil(cssWidth));
  const w2x = snapToBucket(Math.ceil(cssWidth * 2));

  const optimizedSrc = buildResizedUrl(src, physicalWidth);
  const srcSet =
    w1x !== w2x
      ? `${buildResizedUrl(src, w1x)} 1x, ${buildResizedUrl(src, w2x)} 2x`
      : `${buildResizedUrl(src, w1x)} 1x`;

  return (
    <img
      ref={setRef}
      src={optimizedSrc}
      srcSet={srcSet}
      loading={loading}
      decoding={decoding}
      {...rest}
    />
  );
}

export const ResponsiveAsset = ResponsiveAssetImpl;
export default ResponsiveAsset;
